import React, { useState, useEffect } from 'react';
import {
    RiUserAddLine,
    RiDeleteBinLine,
    RiShieldUserLine,
    RiLoader4Line,
     RiTimeLine
} from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL } from '../../config';

const ManageUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [formData, setFormData] = useState({
        username: '',
        password: '',
        role: 'admin'
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/auth/read_users.php`);
            const data = await response.json();
            if (response.ok) {
                setUsers(data.records || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            const response = await fetch(`${API_BASE_URL}/auth/create_user.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            if (response.ok) {
                setMessage({ type: 'success', text: 'Administrative user created successfully!' });
                setFormData({ username: '', password: '', role: 'admin' });
                fetchUsers();
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to create user.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'A network error occurred.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id, name) => {
        if (name === localStorage.getItem('adminUser')) {
            alert("Security Breach Prevention: You cannot delete your currently active session.");
            return;
        }

        if (!window.confirm(`Are you sure you want to revoke administrative access for "${name}"?`)) return;

        try {
            const response = await fetch(`${API_BASE_URL}/auth/delete_user.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchUsers();
            } else {
                const data = await response.json();
                alert(data.message || "Failed to delete user.");
            }
        } catch (error) {
            console.error('Error deleting user:', error);
        }
    };

    return (
        <div className="max-w-[1200px] mx-auto pb-20">
            <div className="mb-10">
                <h2 className="text-4xl font-black text-slate-900 tracking-tight">Access Control</h2>
                <p className="text-slate-500 font-medium mt-2">Manage privileged administrative credentials</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                {/* Registration Form */}
                <div className="lg:col-span-4">
                    <div className="bg-white p-8 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 sticky top-10">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-12 h-12 rounded-2xl bg-emerald-600 flex items-center justify-center text-white shadow-lg shadow-emerald-900/20">
                                <RiUserAddLine size={24} />
                            </div>
                            <div>
                                <h3 className="font-black text-slate-900 leading-tight">Provision User</h3>
                                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">New Administrator</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 pl-1">Unique Alias</label>
                                <input
                                    type="text"
                                    placeholder="Username"
                                    value={formData.username}
                                    onChange={e => setFormData({ ...formData, username: e.target.value })}
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500 transition-all"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 mb-2 pl-1">Pass-Key</label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    value={formData.password}
                                    onChange={e => setFormData({ ...formData, password: e.target.value })}
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500 transition-all"
                                    required
                                />
                            </div>

                            <AnimatePresence>
                                {message.text && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className={`p-4 rounded-2xl text-xs font-bold ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}
                                    >
                                        {message.text}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <button
                                disabled={isSubmitting}
                                className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/20 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isSubmitting ? <RiLoader4Line className="animate-spin" size={20} /> : 'Authorize Credentials'}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Users List */}
                <div className="lg:col-span-8">
                    <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden">
                        <div className="p-8 border-b border-slate-50 flex items-center justify-between">
                            <h3 className="font-black text-slate-900 uppercase tracking-widest text-xs">Registry of Privileged Nodes</h3>
                            <span className="bg-slate-50 px-3 py-1 rounded-full text-[10px] font-black text-slate-400">{users.length} Active Users</span>
                        </div>

                        <div className="divide-y divide-slate-50">
                            {loading ? (
                                <div className="flex justify-center py-20 text-emerald-500">
                                    <RiLoader4Line className="animate-spin text-4xl" />
                                </div>
                            ) : users.length === 0 ? (
                                <div className="py-20 text-center text-slate-400 font-medium">No administrative users found in registry.</div>
                            ) : (
                                users.map((user) => (
                                    <motion.div
                                        key={user.id}
                                        layout
                                        className="p-6 flex items-center justify-between group hover:bg-slate-50/50 transition-colors"
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center text-slate-400 group-hover:bg-white group-hover:shadow-md transition-all">
                                                <RiShieldUserLine size={24} />
                                            </div>
                                            <div>
                                                <h4 className="font-bold text-slate-900 capitalize">{user.username}</h4>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <span className="text-[10px] font-black uppercase tracking-widest text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                                                        {user.role}
                                                    </span>
                                                    <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
                                                        <RiTimeLine />
                                                        {new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <button
                                            onClick={() => handleDelete(user.id, user.username)}
                                            className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                            title="Revoke Access"
                                        >
                                            <RiDeleteBinLine size={20} />
                                        </button>
                                    </motion.div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManageUsers;
